import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ColumnState } from 'ag-grid-community';
import { SharedService } from './shared.service';
import { environment } from 'src/environments/environment';
import { isObjectEmpty } from 'src/app/shared/functions';
import { ComponentNames } from 'src/app/shared/models/constants';

// Friendly labels for toasts - keep in sync with ComponentNames.
const TABLE_DISPLAY_NAMES: Record<string, string> = {
  [ComponentNames.product_services]: 'Products & Services',
  [ComponentNames.expense_table]: 'Expenses Table',
  [ComponentNames.contact_table]: 'Contacts',
  [ComponentNames.sales_table]: 'Sales Table',
  [ComponentNames.bound_book]: 'Bound Book',
  [ComponentNames.ffl_management]: 'FFL Management',
  [ComponentNames.firearm_management]: 'Firearms Management',
  [ComponentNames.bulk_acquisition]: 'Bulk Acquisition',
  [ComponentNames.vendor_dashboard]: 'Distributor Inventory Search',
};

@Injectable({
  providedIn: 'root',
})
export class AgGridService {
  current_session_filtering: {
    component_name: string;
    filters: {};
  }[] = [];

  current_session_column_config: {
    component_name: string;
    column_config: ColumnState[];
  }[] = [];

  constructor(
    private sharedService: SharedService,
    private http: HttpClient,
  ) {}

  /** Whether this component's filters or column layout are already in the
   * in-memory session cache (either loaded from the backend or from the
   * user's own edits this session). */
  hasCachedSettingsFor(component_name: string): boolean {
    return (
      this.current_session_filtering.some(
        (c) => c.component_name === component_name,
      ) ||
      this.current_session_column_config.some(
        (c) => c.component_name === component_name,
      )
    );
  }

  storeCurrentSessionFilters(component_name: string, current_filters: {}) {
    let component = this.current_session_filtering.find(
      (component) => component.component_name === component_name
    );
    if (!component) {
      // If the component is not found, initialize it
      component = {
        component_name: component_name,
        filters: current_filters ? current_filters : {},
      };
      this.current_session_filtering.push(component);
    } else {
      component.filters = current_filters ? current_filters : component.filters;
    }
  }
  storeCurrentSessionColumnConfig(
    component_name: string,
    current_column_config: ColumnState[]
  ) {
    let component = this.current_session_column_config.find(
      (component) => component.component_name === component_name
    );
    if (!component) {
      // If the component is not found, initialize it
      component = {
        component_name: component_name,
        column_config: current_column_config ? current_column_config : [],
      };
      this.current_session_column_config.push(component);
    } else {
      component.column_config = current_column_config
        ? current_column_config
        : component.column_config;
    }
  }
  async saveCurrentTableSettings(component_name: string, filters: {}, column_state: ColumnState[]): Promise<void> {
    const display_name = TABLE_DISPLAY_NAMES[component_name] || component_name;
    try {
      await firstValueFrom(
        this.http.put(
          `${environment.apiUrl}/table_settings/${component_name}`,
          { filters, column_state },
        ),
      );
      this.sharedService.raiseSuccessToast(
        `Saved table settings for ${display_name}`,
      );
    } catch (error) {
      this.sharedService.raiseErrorToast(
        `Failed to save table settings for ${display_name}`,
      );
    }
  }

  /** Fetch this user's saved filters/column layout and seed the in-memory
   * session cache so the existing applyCurrentSessionPreferences() flow in
   * each grid component picks it up without any further changes. */
  async loadSavedTableSettings(component_name: string): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.get<{ filters: {}; column_config: ColumnState[] }>(
          `${environment.apiUrl}/table_settings/${component_name}`,
        ),
      );
      if (!isObjectEmpty(response.filters)) {
        this.storeCurrentSessionFilters(component_name, response.filters);
      }
      if (response.column_config?.length) {
        this.storeCurrentSessionColumnConfig(
          component_name,
          response.column_config,
        );
      }
    } catch (error) {
      // Non-fatal: grid just falls back to its default filters/columns.
    }
  }

  /** Seed the session cache with every saved grid's settings in one call, so
   * individual grids don't each hit the backend on their own onGridReady. */
  async loadAllSavedTableSettings(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.get<
          { component_name: string; filters: {}; column_config: ColumnState[] }[]
        >(`${environment.apiUrl}/table_settings`),
      );
      for (const entry of response) {
        if (!isObjectEmpty(entry.filters)) {
          this.storeCurrentSessionFilters(entry.component_name, entry.filters);
        }
        if (entry.column_config?.length) {
          this.storeCurrentSessionColumnConfig(
            entry.component_name,
            entry.column_config,
          );
        }
      }
    } catch (error) {
      // Non-fatal: each grid falls back to its own on-demand fetch.
    }
  }
}
